import { ArrowDown, type LucideIcon, MinusCircle, X } from "lucide-react";
import type { ReactNode } from "react";
import { ConfChip, LevelChip } from "./chips";
import { C, DM, UI } from "./ui-tokens";

/** Small uppercase section label. */
export function Eyebrow({ children, style }: { children: ReactNode; style?: React.CSSProperties }) {
  return (
    <div
      style={{
        ...UI,
        fontWeight: 600,
        fontSize: 11,
        letterSpacing: "0",
        textTransform: "uppercase",
        color: C.ink50,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** Labelled block: an icon + eyebrow over arbitrary content. */
export function FieldBlock({
  label,
  icon: Icon,
  children,
  style,
}: {
  label: string;
  icon?: LucideIcon;
  children: ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div style={style}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        {Icon && <Icon size={15} color={C.ink50} />}
        <Eyebrow>{label}</Eyebrow>
      </div>
      {children}
    </div>
  );
}

/** Expanded detail card (axis or level) with a paper header bar + close. */
export function DetailShell({
  title,
  badge,
  level,
  nd = false,
  confidence,
  onClose,
  children,
}: {
  title: string;
  badge: string;
  level?: string | null;
  nd?: boolean;
  confidence?: number;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <section
      style={{
        background: C.white,
        border: `1px solid ${C.border}`,
        borderRadius: 16,
        boxShadow: "0 4px 6px -1px rgba(0,0,0,0.08)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "18px 24px",
          borderBottom: `1px solid ${C.borderSoft}`,
          background: C.paper,
        }}
      >
        <ArrowDown size={16} color={C.red} />
        <Eyebrow style={{ color: C.red }}>{badge}</Eyebrow>
        <h3
          style={{
            ...DM,
            fontWeight: 700,
            fontSize: 18,
            color: C.ink,
            margin: 0,
            letterSpacing: "-0.01em",
          }}
        >
          {title}
        </h3>
        <div style={{ flex: 1 }} />
        {nd && (
          <span
            style={{
              ...UI,
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              fontWeight: 500,
              fontSize: 12,
              padding: "3px 10px",
              borderRadius: 999,
              background: C.ink5,
              color: C.ink50,
            }}
          >
            <MinusCircle size={13} /> N/D
          </span>
        )}
        {level && !nd && <LevelChip level={level} state="band" />}
        {confidence != null && <ConfChip value={confidence} />}
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar detalhe"
          style={{
            marginLeft: 6,
            width: 32,
            height: 32,
            borderRadius: 999,
            border: "none",
            background: "transparent",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <X size={20} color={C.ink50} />
        </button>
      </div>
      <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 20 }}>
        {children}
      </div>
    </section>
  );
}
